import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import {
  Drawer,
  Form,
  Upload,
  message,
  Space,
  Button,
  Progress,
  Card,
  List,
  Tag,
  Spin,
  Typography,
  Divider,
  Tooltip,
  Row,
  Popconfirm,
} from "antd";
import {
  InboxOutlined,
  CloseOutlined,
  EyeOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import axios, { CancelTokenSource } from "axios";
import AssetViewerModal from "./AssetViewerModal";
import {
  deleteUploadedAssetOnLecture,
  fetchPresignedUrl,
  fetchUploadedAssetOnLecture,
  GET_CONTENT_LICENCE,
  uploadCallbackUrl,
} from "utilFunctions/Constants/urlConstants";
import {
  getAPIRequest,
  putPostRequest,
  deleteAPI,
} from "utilFunctions/Axios/AxiosRequestConfiguration";
import { ToastContextDispatch, UserInfoContext } from "Context/CommonContext";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { uuid } from "utilFunctions/Axios/AxiosRequest";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";
import {
  getFileContentType,
  isIOSWebKit,
  isMp4File,
  isPdfFile,
} from "utilFunctions/shared/fileUtils";
import { prepareUploadBody } from "utilFunctions/shared/prepareUploadBody";
const { Text } = Typography;
interface UploadAssetDrawerProps {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: (data: any) => void;
  BASE_URL: string;
  lectureId?: string;
  courseId?: string;
  prevContent?: any; // Previous content to check if video upload is enabled
  videoAccess: string[];
  pdfAccess: string[];
}

interface IsVideoUploadEable {
  lectureUploadId?: string | undefined;
  isEnable?: boolean;
}

interface FileProgress {
  file: File;
  progress: number;
  cancelToken: CancelTokenSource;
  remainingTime: string;
  isUploading: boolean;
}

const returnTagColor = (status: string) => {
  switch (status) {
    case "Processing":
      return "blue";
    case "Uploading":
      return "orange";
    case "Uploaded":
      return "cyan";
    case "Failed":
      return "red";
    default:
      return "green";
  }
};

const UploadAssetDrawer: React.FC<UploadAssetDrawerProps> = ({
  open,
  onClose,
  onUploadSuccess,
  BASE_URL,
  lectureId,
  courseId,
  prevContent,
  videoAccess,
  pdfAccess,
}) => {
  const [form] = Form.useForm();
  const userInfo = useContext<IUserInfo>(UserInfoContext);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [videoProgress, setVideoProgress] = useState<number>(0);
  const [isVideoUploading, setIsVideoUploading] = useState<boolean>(false);
  const [isPDFUploading, setIsPDFUploading] = useState<boolean>(false);
  const [videoCancelToken, setVideoCancelToken] =
    useState<CancelTokenSource | null>(null);
  const ToastDispatch = useContext(ToastContextDispatch);
  const { screen }: any = useWindowSize();
  const isIOS = useMemo(() => isIOSWebKit(), []);

  const canUploadVideo = videoAccess.includes("POST");
  const canUpdateVideo = videoAccess.includes("PUT");
  const canDeleteVideo = videoAccess.includes("DELETE");

  const canUploadPdf = pdfAccess.includes("POST");
  const canUpdatePdf = pdfAccess.includes("PUT");
  const canDeletePdf = pdfAccess.includes("DELETE");

  const [videoData, setVideoData] = useState<any>(
    prevContent && prevContent.videos && prevContent.videos.length
      ? prevContent.videos[0]
      : null
  );
  const [pdfData, setPdfData] = useState<any[]>(
    prevContent && prevContent.ebooks && prevContent.ebooks.length
      ? prevContent.ebooks
      : []
  );
  const [pdfUploads, setPdfUploads] = useState<FileProgress[]>([]);
  const pdfUploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [videoMultipartdata, setVideoMultipartdata] = useState<any>(null);
  const [isViewerModalOpen, setIsViewerModalOpen] = useState(false);
  const [viewerContentType, setViewerContentType] = useState<{
    type: "video" | "ebook" | "recordedPDF" | "recordedVideo" | null;
    data: string;
  } | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string | undefined>("");
  const [viewerAssetId, setViewerAssetId] = useState<string | undefined>("");
  const [pdfPassword, setPdfPassword] = useState<string>("");
  const [lectureUploadID, setLectureUploadID] = useState<IsVideoUploadEable>({
    lectureUploadId: undefined,
    isEnable: true,
  });
  const [videoIsDeleting, setvideoIsDeleting] = useState<boolean>(false);
  const [pdfDeletingList, setPdfDeletingList] = useState<string[]>([]);
  const { makeRequest: fetchFileDecryptionKey } = useGetApi(
    (data: any) => {
      if (data?.detailMessage) {
        const decodedKey = atob(data?.detailMessage);
        setPdfPassword(decodedKey);
      } else {
        setPdfPassword("NO_PASSWORD");
      }
    },
    (error: any) => {
      setPdfPassword("NO_PASSWORD");
      console.error(error);
    }
  );

  useEffect(() => {
    if (open) {
      fetchUploadedFiles();
    }
  }, [open]);
  useEffect(() => {
    if (prevContent && prevContent.videos) {
      const video = prevContent.videos[0];
      if (video) {
        setVideoData(video);
      }
    } else if (prevContent && prevContent.ebooks) {
      const pdfs = prevContent.ebooks;
      if (pdfs.length > 0) {
        setPdfData(pdfs);
      }
    } else {
      setVideoData(null);
      setPdfData([]);
    }
  }, [prevContent]);

  const fetchUploadedFiles = async () => {
    try {
      setIsSaving(true);
      getAPIRequest({
        url: `${BASE_URL + fetchUploadedAssetOnLecture}?id=${lectureId}`,
        success: (response: any) => {
          if (response.data?.data) {
            const video = response.data.data.find(
              (item: any) => item.assetType === "Video"
            );
            const pdf = response.data.data.filter(
              (item: any) => item.assetType === "Ebook"
            );
            if (video) {
              const nondeleted = response.data.data.find(
                (item: any) =>
                  item.assetType === "Video" && item.status != "Deleted"
              );
              if (nondeleted == undefined) {
                setLectureUploadID({
                  lectureUploadId: video.lectureUploadId,
                  isEnable: video?.uploadEnable,
                });
              }
              setVideoData(nondeleted);
            }
            if (pdf.length > 0) {
              setPdfData((prevPdf) => {
                const getUrl = (item: any) =>
                  item.url || item.lectureUrl || item.lectureContentUrl;
                const getId = (item: any) => item.lectureUploadId;

                const newPdfUrlMap = new Map(
                  pdf.map((item: any) => [getUrl(item), item])
                );
                const newPdfIdMap = new Map(
                  pdf.map((item: any) => [getId(item), item])
                );

                const filteredPrevPdf = prevPdf.filter((item: any) => {
                  const url = getUrl(item);
                  const id = getId(item);
                  return !newPdfUrlMap.has(url) && !newPdfIdMap.has(id);
                });

                return [...filteredPrevPdf, ...pdf];
              });
            }

            // Check if any asset is still processing
            const isProcessing = response.data.data.some(
              (item: any) => item.status === "Processing"
            );

            // If any asset is processing, call the function again after 5 seconds
            if (isProcessing) {
              setTimeout(fetchUploadedFiles, 5000);
            }
          }
          setIsSaving(false);
        },
        failure: (data: any) => {
          ToastDispatch({
            isOpen: true,
            type: "Error",
            message: data["message"],
          });
        },
      });
    } catch (error) {
      message.error("Failed to fetch uploaded files");
    }
  };
  console.log("nondeleted", lectureUploadID, "lectureUploadID");

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const SaveButton = async () => {
    if (!selectedVideo && pdfUploads.length === 0) {
      return message.error("Please select at least one file (Video or PDFs)");
    }

    setIsSaving(true);

    setPdfUploads((prevUploads) =>
      prevUploads.map((upload) => ({
        ...upload,
        isUploading: true,
      }))
    );
    // Keep uploads sequential so iOS does not buffer multiple large request
    // bodies in memory at the same time.
    if (selectedVideo) {
      await Promise.allSettled([uploadFile(selectedVideo, "Video")]);
    }
    for (const upload of pdfUploads) {
      await Promise.allSettled([
        uploadFile(upload.file, "Ebook", upload.cancelToken),
      ]);
    }

    const uploadedAssetTypes: string[] = [];
    if (selectedVideo) uploadedAssetTypes.push("Video");
    if (pdfUploads.length) uploadedAssetTypes.push("Ebook");
    pushToDataLayerForGTM({
      event: "ag_lecture_upload_asset_from_schedule",
      ASSET_TYPE: uploadedAssetTypes.length
        ? uploadedAssetTypes.join(",")
        : null,
      COUNT: (selectedVideo ? 1 : 0) + pdfUploads.length,
    });

    // Fetch updated data from the server
    await fetchUploadedFiles();

    // Reset saving state
    setIsSaving(false);
    setPdfUploads([]);
    // message.success("Upload process completed!");
  };

  // Add utility functions for multipart upload
  const createMultipartUpload = async (
    lectureId: string | undefined,
    fileName: string,
    assetType: string,
    noOfParts: number,
    lectureUploadId: string | undefined
  ) => {
    const response = await axios.post(
      `${BASE_URL}uploadLectureMedia/getMultipartUploadUrls`,
      {
        lectureId,
        fileName,
        assetType,
        noOfParts,
        lectureUploadId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${localStorage.getItem("token")}`,
          reqMsgId: uuid(),
        },
      }
    );
    console.log("objectKey", response?.data?.data);
    return response?.data?.data; // { uploadId, key }
  };

  const completeMultipartUpload = async (
    lectureUploadId: string,
    uploadId: string,
    objectKey: string,
    entityTagsList: { tags: string; partNumber: number }[]
  ) => {
    const response = await axios.post(
      `${BASE_URL}uploadLectureMedia/completeMultipartUpload`,
      {
        lectureUploadId,
        uploadId,
        objectKey,
        entityTagsList,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${localStorage.getItem("token")}`,
          reqMsgId: uuid(),
        },
      }
    );
    return response.data?.data;
  };

  // Helper function to upload a single part with retry mechanism
  const uploadPart = async (
    url: string,
    blob: Blob,
    cancelToken?: CancelTokenSource,
    onProgress?: (progress: number) => void,
    contentType = blob.type || "application/octet-stream",
    maxRetries = 3 // Maximum number of retries
  ): Promise<string> => {
    cancelToken?.token.throwIfRequested();
    const body = await prepareUploadBody(blob, contentType);
    cancelToken?.token.throwIfRequested();
    return new Promise((resolve, reject) => {
      let attempt = 0;
      let settled = false;
      let activeXhr: XMLHttpRequest;

      const tryUpload = () => {
        if (cancelToken?.token.reason) {
          settled = true;
          reject(cancelToken.token.reason);
          return;
        }
        const xhr = new XMLHttpRequest();
        activeXhr = xhr;
        xhr.open("PUT", url, true);
        xhr.setRequestHeader("Content-Type", contentType);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && onProgress) {
            onProgress(event.loaded);
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            settled = true;
            const Etag = xhr.getResponseHeader("Etag");
            resolve(Etag || "");
          } else if (attempt < maxRetries) {
            attempt++;
            console.warn(`Retrying part upload (Attempt ${attempt})...`);
            tryUpload(); // Retry the upload
          } else {
            settled = true;
            reject(
              new Error(`Failed to upload part after ${maxRetries} attempts`)
            );
          }
        };

        xhr.onerror = () => {
          if (attempt < maxRetries) {
            attempt++;
            console.warn(`Retrying part upload (Attempt ${attempt})...`);
            tryUpload(); // Retry the upload
          } else {
            settled = true;
            reject(new Error("Failed to upload part"));
          }
        };

        if (cancelToken) {
          cancelToken.token.promise.then(() => {
            if (settled || activeXhr !== xhr) return;
            settled = true;
            xhr.abort();
            reject(new Error("Upload canceled"));
          });
        }

        xhr.send(body);
      };

      tryUpload(); // Start the first upload attempt
    });
  };

  // Helper function for single-part upload (existing logic)
  const uploadFileSinglePart = (
    file: File,
    type: "Video" | "Ebook",
    cancelToken?: CancelTokenSource,
    lectureUploadid?: string | undefined
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const bodyData: any = {
        fileName: file.name,
        assetType: type,
        lectureId: lectureId,
        lectureUploadId: type == "Video" ? lectureUploadid : undefined,
      };
      console.log(lectureUploadid, "lectureUploadID370", cancelToken);
      const token = cancelToken || axios.CancelToken.source();
      const method = lectureUploadid ? "PUT" : "POST";
      putPostRequest({
        url: `${BASE_URL + fetchPresignedUrl}`,
        success: (res: any) => {
          if (!res?.data?.data?.preSignedUrl) {
            reject(
              new Error("Could not prepare the upload. Please try again.")
            );
            return;
          }
          if (res?.data?.data) {
            const { preSignedUrl, lectureUploadId } = res.data.data;
            handleUploadOnS3(
              preSignedUrl,
              lectureUploadId,
              file,
              bodyData,
              type,
              token
            )
              .then(resolve) // Resolve after handleUploadOnS3 completes
              .catch(reject); // Reject if handleUploadOnS3 fails
          }
        },
        failure: (err: any) => {
          setIsSaving(false);
          message.error(err.response?.data?.message || `${type} upload failed`);
          setVideoProgress(0);
          setSelectedVideo(null);
          setVideoCancelToken(null);
          setIsVideoUploading(false);
          form.setFieldValue("video", null);
          reject(err); // Reject the promise on failure
        },
        body: bodyData,
        method: method,
      });
    });
  };

  // Update the uploadFile method to handle multipart uploads for files > 200MB
  const uploadFile = async (
    file: File,
    type: "Video" | "Ebook",
    cancelToken?: CancelTokenSource
  ): Promise<void> => {
    let token = cancelToken;
    if (!token) {
      token = axios.CancelToken.source();
      if (type === "Video") {
        setVideoCancelToken(token);
      }
    }
    if (type === "Video") {
      setIsVideoUploading(true);
    } else {
      setIsPDFUploading(true);
    }
    const singlePartLimit = (isIOS ? 10 : 100) * 1024 * 1024;
    try {
      if (file.size <= singlePartLimit) {
        await uploadFileSinglePart(
          file,
          type,
          token,
          type === "Video" ? lectureUploadID.lectureUploadId : undefined
        );
        return;
      }

      // For larger files, read and upload one 10MB chunk at a time.
      const chunkSize = 10 * 1024 * 1024;
      const partsCount = Math.ceil(file.size / chunkSize);
      // Step 1: Create multipart upload
      const { lectureUploadId, objectKey, preSignedUrls, uploadId } =
        await createMultipartUpload(
          lectureId,
          file.name,
          type,
          partsCount,
          lectureUploadID.lectureUploadId
        );
      if (
        !Array.isArray(preSignedUrls) ||
        preSignedUrls.length !== partsCount
      ) {
        throw new Error(
          "Could not prepare all upload parts. Please try again."
        );
      }
      setVideoMultipartdata({
        lectureUploadId,
        objectKey,
        uploadId,
        fileName: file.name,
        assetType: type,
        status: "Uploading",
      });
      setIsVideoUploading(true);
      setVideoProgress(0);
      const entityTagsList: { tags: string; partNumber: number }[] = [];
      let uploadedParts = 0;

      // Step 2: Upload each part
      for (let i = 0; i < partsCount; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);

        const { preSignedUrl, partNumber } = preSignedUrls[i];
        const tags = await uploadPart(
          preSignedUrl,
          blob,
          token,
          undefined,
          getFileContentType(file)
        );

        // Increment the count of uploaded parts
        uploadedParts += 1;

        // Calculate overall progress based on parts uploaded
        const overallProgress = Math.round((uploadedParts / partsCount) * 100);

        if (type === "Video") {
          setVideoProgress(overallProgress);
        } else {
          setPdfUploads((prevUploads) =>
            prevUploads.map((upload) =>
              upload.file.name === file.name
                ? { ...upload, progress: overallProgress }
                : upload
            )
          );
        }

        entityTagsList.push({ tags: JSON.parse(tags), partNumber: partNumber });
      }

      const bodyData: any = {
        fileName: file.name,
        assetType: type,
        lectureId: lectureId,
      };

      // Step 3: Complete multipart upload
      token.token.throwIfRequested();
      await completeMultipartUpload(
        lectureUploadId,
        uploadId,
        objectKey,
        entityTagsList
      );
      await handleURLCallback(bodyData, lectureUploadId, type, file.name);
      message.success(`${type} Upload process completed!`);
    } catch (error: any) {
      message.error(error.message || `Failed to upload ${type}: ${file.name}`);
      setIsSaving(false);
      if (type === "Video") {
        setIsVideoUploading(false);
        setVideoMultipartdata(null);
        setVideoProgress(0);
        form.setFieldValue("video", null); // Reset the video field in the form
      } else {
        setPdfUploads((prevUploads) =>
          prevUploads.map((upload) =>
            upload.file.name === file.name
              ? { ...upload, progress: 0, isUploading: false }
              : upload
          )
        );
        setIsPDFUploading(false);
      }
      throw error;
    }
  };

  const handleUploadOnS3 = async (
    url: string,
    lectureUploadId: string,
    file: File,
    bodyData: any,
    type: "Video" | "Ebook",
    cancelToken: CancelTokenSource
  ): Promise<void> => {
    cancelToken.token.throwIfRequested();
    const body = await prepareUploadBody(file, getFileContentType(file));
    cancelToken.token.throwIfRequested();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;
      let fakeProgress = 0;
      const increment = Math.floor(Math.random() * 5 + 3);
      xhr.open("PUT", url, true);
      xhr.setRequestHeader("Content-Type", getFileContentType(file));

      const progressInterval = setInterval(() => {
        fakeProgress += increment;
        if (fakeProgress >= 85) {
          clearInterval(progressInterval);
        }

        if (type === "Video") {
          setVideoProgress(fakeProgress);
        } else {
          setPdfUploads((prevUploads) =>
            prevUploads.map((upload) =>
              upload.file.name === file.name
                ? { ...upload, progress: fakeProgress }
                : upload
            )
          );
        }
      }, 500); // Update progress every 500ms

      // Handle upload completion
      xhr.onload = () => {
        settled = true;
        clearInterval(progressInterval); // Stop fake progress
        if (xhr.status === 200) {
          if (type === "Video") {
            setVideoProgress(100); // Set progress to 100% on success
          } else {
            setPdfUploads((prevUploads) =>
              prevUploads.map((upload) =>
                upload.file.name === file.name
                  ? { ...upload, progress: 100, isUploading: false }
                  : upload
              )
            );
          }
          handleURLCallback(bodyData, lectureUploadId, type, file.name)
            .then(resolve)
            .catch(reject);
        } else {
          message.error(`${type.toUpperCase()}: ${file.name} upload failed`);
          deleteFile(
            type.toLowerCase() as "video" | "pdf",
            lectureUploadId,
            file.name
          );
          reject(new Error(`Failed to upload ${type}: ${file.name}`));
        }
      };

      // Handle upload error
      xhr.onerror = () => {
        settled = true;
        clearInterval(progressInterval); // Stop fake progress
        message.error(`${type.toUpperCase()}: ${file.name} upload failed`);
        deleteFile(
          type.toLowerCase() as "video" | "pdf",
          lectureUploadId,
          file.name
        );
        reject(new Error(`Failed to upload ${type}: ${file.name}`));
      };

      // Handle upload cancellation
      cancelToken.token.promise.then(() => {
        if (settled) return;
        settled = true;
        xhr.abort();
        clearInterval(progressInterval); // Stop fake progress
        message.info(`${type.toUpperCase()} upload canceled`);
        if (type === "Video") {
          setIsVideoUploading(false);
          deleteFile("video", lectureUploadId);
          setVideoMultipartdata(null);
          form.setFieldValue("video", null); // Reset the video field in the form
        } else {
          setPdfUploads((prevUploads) =>
            prevUploads.map((upload) =>
              upload.file.name === file.name
                ? { ...upload, progress: 0, isUploading: false }
                : upload
            )
          );
          deleteFile("pdf", lectureUploadId, file.name);
        }
        reject(new Error("Upload canceled"));
      });

      // Start the upload
      xhr.send(body);
    });
  };

  const handleURLCallback = (
    data: any,
    lectureUploadId: string,
    type: "Video" | "Ebook",
    fileName?: string
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const bodydata = {
        ...data,
        lectureUploadId: lectureUploadId,
      };
      putPostRequest({
        url: `${BASE_URL + uploadCallbackUrl}`,
        success: (res: any) => {
          if (type === "Video") {
            setVideoProgress(0);
            setSelectedVideo(null);
            setVideoCancelToken(null);
            setIsVideoUploading(false);
            form.setFieldValue("video", null); // Reset the video field in the form
            const vidData = {
              ...data,
              lectureUploadId: lectureUploadId,
              status: "Uploaded",
              fileName: fileName,
            };
            setVideoData(vidData); // Update video data
            message.success(`${type} Upload process completed!`);
          } else {
            const pdfData = {
              ...data,
              lectureUploadId: lectureUploadId,
              status: "Uploaded",
              fileName: fileName,
            };
            form.setFieldValue("pdf", null); // Reset the PDF field in the form
            setIsPDFUploading(false);
            setPdfUploads((prevUploads) =>
              prevUploads.filter((upload) => upload.file.name !== fileName)
            );
            setPdfData((prevData) => [...prevData, pdfData]); // Add uploaded PDF data
            message.success(`${type} Upload process completed!`);
          }
          setIsSaving(false);
          onUploadSuccess(res.data);
          resolve(); // Resolve the promise on success
        },
        failure: (err: any) => {
          if (type === "Video") {
            setVideoProgress(0);
            setSelectedVideo(null);
            setVideoCancelToken(null);
            setIsVideoUploading(false);
            setVideoMultipartdata(null);
          } else {
            setIsPDFUploading(false);
            setPdfUploads((prevUploads) =>
              prevUploads.filter((upload) => upload.file.name !== fileName)
            );
          }
          setIsSaving(false);
          message.error(err.response?.data?.message || "Callback failed");
          reject(err); // Reject the promise on failure
        },
        body: bodydata,
        method: "Post",
      });
    });
  };

  const cancelUpload = async (type: "video" | "pdf", fileName?: string) => {
    if (type === "video" && videoCancelToken) {
      videoCancelToken.cancel();
      if (
        videoMultipartdata?.lectureUploadId &&
        videoMultipartdata?.uploadId &&
        videoMultipartdata?.objectKey
      ) {
        try {
          await axios.post(
            `${BASE_URL}uploadLectureMedia/abortMultipartUpload`,
            {
              lectureUploadId: videoMultipartdata.lectureUploadId,
              uploadId: videoMultipartdata.uploadId,
              objectKey: videoMultipartdata.objectKey,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `${localStorage.getItem("token")}`,
                reqMsgId: uuid(),
              },
            }
          );
          message.success("Video upload canceled successfully.");
          deleteFile("video", videoMultipartdata.lectureUploadId);
          setVideoMultipartdata(null);
          setVideoProgress(0);
          setSelectedVideo(null);
          setVideoCancelToken(null);
          setIsVideoUploading(false);
          form.setFieldValue("video", null);
        } catch (error) {
          message.error("Failed to cancel video upload on the server.");
        }
      } else {
        setVideoProgress(0);
        setSelectedVideo(null);
        setVideoCancelToken(null);
        setIsVideoUploading(false);
        form.setFieldValue("video", null);
      }
      // Cancel the video upload locally

      // Call the abortMultipartUpload API for video
    } else if (type === "pdf" && fileName) {
      // Cancel the PDF upload locally
      setPdfUploads((prevUploads) => {
        const uploadToCancel = prevUploads.find(
          (upload) => upload.file.name === fileName
        );
        if (uploadToCancel) {
          uploadToCancel.cancelToken.cancel();
        }
        return prevUploads.filter((upload) => upload.file.name !== fileName);
      });

      // Call the abortMultipartUpload API for PDF
      // const pdfToCancel = pdfUploads.find((upload) => upload.file.name === fileName);
      // if (pdfToCancel?.lectureUploadId && pdfToCancel?.uploadId && pdfToCancel?.objectKey) {
      //   try {
      //     await axios.post(
      //       `${BASE_URL}uploadLectureMedia/abortMultipartUpload`,
      //       {
      //         lectureUploadId: pdfToCancel.lectureUploadId,
      //         uploadId: pdfToCancel.uploadId,
      //         objectKey: pdfToCancel.objectKey,
      //       },
      //       {
      //         headers: {
      //           "Content-Type": "application/json",
      //           Authorization: `${localStorage.getItem("token")}`,
      //           reqMsgId: uuid(),
      //         },
      //       }
      //     );
      //     message.success(`PDF upload for "${fileName}" canceled successfully.`);
      //   } catch (error) {
      //     message.error(`Failed to cancel PDF upload for "${fileName}" on the server.`);
      //   }
      // }
    }
  };

  const deleteFile = async (
    type: "video" | "pdf" | "recordedVideo" | "recordedPDF",
    id: any,
    fileName?: string,
    isDeletingPdf?: boolean
  ) => {
    try {
      if (type === "video" || type === "recordedVideo") {
        const isCrystal = type === "recordedVideo";
        // Delete video file
        deleteAPI({
          url: `${
            BASE_URL + deleteUploadedAssetOnLecture
          }?id=${id}&fromExternal=${isCrystal}`,
          success: () => {
            setvideoIsDeleting(true);
            setTimeout(() => {
              message.success(
                `${type.toUpperCase()} file deleted successfully`
              );
              setVideoData(null);
              setSelectedVideo(null);
              setVideoProgress(0);
              form.setFieldValue("video", null);
              setIsVideoUploading(false);
              setVideoMultipartdata(null);
              setvideoIsDeleting(false);
              fetchUploadedFiles();
            }, 10000);
          },
          failure: (error: any) => {
            message.error(
              error.response?.data?.message || `Failed to delete ${type} file`
            );
          },
          body: { fileType: type },
        });
      } else if ((type === "pdf" && fileName) || type === "recordedPDF") {
        // Delete specific PDF file
        const isCrystal = type === "recordedPDF";
        deleteAPI({
          url: `${
            BASE_URL + deleteUploadedAssetOnLecture
          }?id=${id}&fromExternal=${isCrystal}`,
          success: () => {
            setTimeout(() => {
              if (isDeletingPdf) {
                setPdfDeletingList((prevList) =>
                  prevList.filter((name) => name !== fileName)
                );
              }
              message.success(`PDF file "${fileName}" deleted successfully`);
              if (isCrystal) {
                setPdfData((prevData) =>
                  prevData.filter((pdf) => pdf.id !== id)
                );
              } else {
                setPdfData((prevData) =>
                  prevData.filter((pdf) => pdf.fileName !== fileName)
                );
              }
            }, 5000);
          },
          failure: (error: any) => {
            message.error(
              error.response?.data?.message || "Failed to delete PDF file"
            );
            if (isDeletingPdf) {
              setPdfDeletingList((prevList) =>
                prevList.filter((name) => name !== fileName)
              );
            }
          },
          body: { fileType: type, fileName },
        });
      }
    } catch (error) {
      message.error(`Unexpected error occurred while deleting ${type} file`);
    }
  };

  const handleClose = () => {
    // Reset the form fields
    form.resetFields();
    // setPdfData([]);
    // setVideoData(null);
    // Clear state variables
    setSelectedVideo(null);
    setVideoProgress(0);
    setVideoCancelToken(null);
    setVideoMultipartdata(null);
    setPdfUploads([]);
    setIsSaving(false);

    // Call the parent-provided onClose function
    onClose();
  };

  const openViewerModal = async (
    type: "video" | "ebook" | "recordedVideo" | "recordedPDF",
    data: string,
    title?: string,
    assetId?: string
  ) => {
    if ((type === "ebook" || type === "recordedPDF") && assetId) {
      // Fetch the PDF password
      const source = type === "recordedPDF" ? "CRYSTAL" : "FEX";
      await fetchFileDecryptionKey(
        BASE_URL +
          GET_CONTENT_LICENCE +
          `?assetId=${lectureId}&courseId=${courseId}&source=${source}`
      );
    }

    // Open the modal after fetching the password
    setViewerContentType({ type, data });
    setViewerTitle(title);
    setViewerAssetId(assetId);
    setIsViewerModalOpen(true);
  };

  const closeViewerModal = () => {
    setViewerContentType(null);
    setViewerTitle("");
    setViewerAssetId("");
    setIsViewerModalOpen(false);
  };
  console.log(
    isPDFUploading,
    viewerAssetId,
    "contentType",
    pdfUploads,
    selectedVideo,
    videoProgress,
    userInfo
  );
  return (
    <>
      <Drawer
        title="Upload Lecture Recording and Lecture notes"
        width={600}
        closable={true}
        open={open}
        onClose={handleClose}
        footer={
          <Space style={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              loading={
                !!(
                  isVideoUploading ||
                  videoIsDeleting ||
                  (pdfUploads.length &&
                    pdfUploads.some((upload) => upload.isUploading))
                )
              }
              type="primary"
              htmlType="submit"
              onClick={SaveButton}
              disabled={!selectedVideo && pdfUploads.length === 0}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
          </Space>
        }
      >
        <Form
          layout="vertical"
          labelWrap
          form={form}
          name="control-upload"
          onFinish={SaveButton}
        >
          {videoData ? (
            <Card title="Video File" style={{ marginBottom: 16 }}>
              <List
                itemLayout="horizontal"
                dataSource={[videoData]}
                renderItem={(video) => {
                  return (
                    <List.Item
                      style={{ display: "flex", alignItems: "center" }}
                      actions={[
                        (video.status == "Live" ||
                          video?.client == "CRYSTAL") && (
                          <Button
                            key={video.fileName + "view"}
                            type="link"
                            icon={<EyeOutlined />}
                            onClick={() =>
                              openViewerModal(
                                video?.client == "CRYSTAL"
                                  ? "recordedVideo"
                                  : "video",
                                video?.client == "CRYSTAL"
                                  ? video?.url
                                  : video.lectureContentUrl,
                                video?.client == "CRYSTAL"
                                  ? "Recorded Lecture Video"
                                  : video.fileName,
                                video?.client == "CRYSTAL"
                                  ? video?.id
                                  : video.lectureUploadId
                              )
                            }
                          ></Button>
                        ),
                        <Popconfirm
                          key={video.fileName + "delete_pop"}
                          title="Are you sure you want to delete this video?"
                          onConfirm={() =>
                            deleteFile(
                              video?.client == "CRYSTAL"
                                ? "recordedVideo"
                                : "video",
                              video?.client == "CRYSTAL"
                                ? video?.id
                                : video.lectureUploadId
                            )
                          }
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button
                            key={video.fileName + "delete"}
                            type="link"
                            icon={<DeleteOutlined />}
                            danger
                            loading={videoIsDeleting}
                            disabled={!canDeleteVideo && !canUpdateVideo}
                          ></Button>
                        </Popconfirm>,
                      ]}
                    >
                      <Row
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Tooltip
                          title={video.fileName ?? "Recorded Lecture Video"}
                        >
                          <span
                            style={{
                              fontWeight: "bold",
                              marginRight: "10px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: ["xs", "sm", "md"].includes(screen)
                                ? "50%"
                                : "70%",
                            }}
                          >
                            {video.fileName ?? "Recorded Lecture Video"}
                          </span>
                        </Tooltip>

                        <Tag color={returnTagColor(video.status)}>
                          {video.status ?? "Live"}
                        </Tag>
                      </Row>
                    </List.Item>
                  );
                }}
              />
            </Card>
          ) : (
            <>
              {videoProgress > 0 || isVideoUploading ? (
                <Card style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Spin size="large" indicator={<LoadingOutlined spin />} />
                    <div style={{ width: "100%", marginLeft: "10px" }}>
                      <Text>{selectedVideo?.name}</Text>
                      {videoProgress > 0 && (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                          }}
                        >
                          <Progress
                            percent={videoProgress}
                            status={
                              videoProgress === 100 ? "success" : "active"
                            }
                            style={{ width: "100%" }}
                          />
                          <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={() => cancelUpload("video")}
                            style={{ marginLeft: "10px" }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ) : (
                <>
                  {canUploadVideo && (
                    <Form.Item
                      name="video"
                      valuePropName="fileList"
                      getValueFromEvent={normFile}
                      rules={[
                        {
                          required: !selectedVideo,
                          message: "Please choose a video file",
                        },
                      ]}
                    >
                      <Upload.Dragger
                        name="video"
                        accept=".mp4,video/mp4"
                        multiple={false}
                        maxCount={1}
                        disabled={!lectureUploadID?.isEnable}
                        onRemove={() => {
                          setSelectedVideo(null);
                          setVideoProgress(0);
                          setVideoCancelToken(null);
                          setIsVideoUploading(false);
                          form.setFieldValue("video", null);
                        }}
                        beforeUpload={(file) => {
                          const isLt2GB = file.size / 1024 / 1024 < 2024; // 2GB limit
                          if (!isMp4File(file)) {
                            message.error("You can only upload video files!");
                            return Upload.LIST_IGNORE;
                          }
                          if (!isLt2GB) {
                            message.error("Video must be smaller than 2GB!");
                            return Upload.LIST_IGNORE;
                          } else {
                            setSelectedVideo(file);
                          }
                          return false; // Prevent auto-upload
                        }}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                          Select a Video to upload
                        </p>
                        <p className="ant-upload-hint">
                          Maximum file size 2GB, format .mp4
                        </p>
                      </Upload.Dragger>
                    </Form.Item>
                  )}
                </>
              )}
            </>
          )}

          {pdfData.length > 0 ? (
            <Card title="PDF Files">
              <>
                <List
                  itemLayout="horizontal"
                  dataSource={pdfData}
                  renderItem={(pdf, index) => {
                    return (
                      <List.Item
                        style={{ display: "flex", alignItems: "center" }}
                        actions={[
                          (pdf.status == "Live" ||
                            pdf?.client == "CRYSTAL") && (
                            <Button
                              key={pdf.fileName + "view"}
                              type="link"
                              icon={<EyeOutlined />}
                              onClick={() =>
                                openViewerModal(
                                  pdf?.client == "CRYSTAL"
                                    ? "recordedPDF"
                                    : "ebook",
                                  pdf?.client == "CRYSTAL"
                                    ? pdf?.url
                                    : pdf.lectureContentUrl,
                                  pdf?.client == "CRYSTAL"
                                    ? "Recorded Lecture Notes PDF"
                                    : pdf.fileName,
                                  pdf?.client == "CRYSTAL"
                                    ? pdf?.id
                                    : pdf.lectureUploadId
                                )
                              } // Open modal with PDF data
                            ></Button>
                          ),
                          <Popconfirm
                            key={pdf.fileName + "delete_pop"}
                            title="Are you sure you want to delete this PDF?"
                            onConfirm={() => {
                              setPdfDeletingList((prev) => [
                                ...prev,
                                pdf.fileName,
                              ]);
                              deleteFile(
                                pdf?.client == "CRYSTAL"
                                  ? "recordedPDF"
                                  : "pdf",
                                pdf?.client == "CRYSTAL"
                                  ? pdf.id
                                  : pdf.lectureUploadId,
                                pdf?.client == "CRYSTAL"
                                  ? "Recorded Lecture Notes PDF " + index + 1
                                  : pdf.fileName,
                                true
                              );
                            }}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              key={pdf.fileName + "delete"}
                              type="link"
                              icon={<DeleteOutlined />}
                              loading={pdfDeletingList.includes(pdf.fileName)}
                              disabled={!canDeletePdf && !canUpdatePdf}
                              danger
                            ></Button>
                          </Popconfirm>,
                        ]}
                      >
                        <Row
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Tooltip title={pdf.fileName}>
                            <span
                              style={{
                                fontWeight: "bold",
                                marginRight: "10px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: ["xs", "sm", "md"].includes(screen)
                                  ? "50%"
                                  : "70%",
                              }}
                            >
                              {pdf.fileName ?? "Lecture Notes PDF " + index + 1}
                            </span>
                          </Tooltip>
                          <Tag color={returnTagColor(pdf.status)}>
                            {pdf.status ?? "Live"}
                          </Tag>
                        </Row>
                      </List.Item>
                    );
                  }}
                />
                <div style={{ marginTop: 16 }}>
                  {canUploadPdf && (
                    <Upload
                      name="pdf"
                      accept=".pdf,application/pdf"
                      multiple={true}
                      maxCount={10}
                      showUploadList={false}
                      onRemove={(file) => {
                        setPdfUploads((prevUploads) =>
                          prevUploads.filter(
                            (upload) => upload.file.name !== file.name
                          )
                        );
                      }}
                      beforeUpload={async (file, fileList) => {
                        if (fileList.length > 10) {
                          return Upload.LIST_IGNORE;
                        }

                        const isLt10MB = file.size / 1024 / 1024 < 50;
                        if (!isPdfFile(file)) {
                          message.error("You can only upload PDF files!");
                          return Upload.LIST_IGNORE;
                        }
                        if (!isLt10MB) {
                          message.error("PDF must be smaller than 50MB!");
                          return Upload.LIST_IGNORE;
                        }

                        // Trigger the uploadFile function directly
                        const cancelToken = axios.CancelToken.source();
                        setPdfUploads((prevUploads) => [
                          ...prevUploads,
                          {
                            file,
                            progress: 0,
                            cancelToken,
                            remainingTime: "Calculating...",
                            isUploading: true,
                          },
                        ]);

                        // Trigger the uploadFile function directly
                        pdfUploadQueueRef.current = pdfUploadQueueRef.current
                          .catch(() => undefined)
                          .then(async () => {
                            await uploadFile(file, "Ebook", cancelToken);
                            // Fetch updated data from the server after upload
                            await fetchUploadedFiles();
                          })
                          .catch(() => {
                            // Handle upload failure
                            setPdfUploads((prevUploads) =>
                              prevUploads.filter(
                                (upload) => upload.file.name !== file.name
                              )
                            );
                          });

                        return false; // Prevent auto-upload by Upload component
                      }}
                    >
                      <Button type="dashed" icon={<InboxOutlined />}>
                        Upload More
                      </Button>
                    </Upload>
                  )}
                </div>
              </>
            </Card>
          ) : (
            <>
              {canUploadPdf && (
                <Form.Item
                  name="pdf"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  rules={[
                    {
                      validator: () => {
                        if (pdfUploads.length > 10) {
                          return Promise.reject(
                            new Error("You can only upload up to 10 PDF files!")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                    {
                      required: !pdfUploads.length,
                      message: "Please choose PDFs files",
                    },
                  ]}
                >
                  {!isSaving && (
                    <Upload.Dragger
                      name="pdf"
                      accept=".pdf,application/pdf"
                      multiple={true}
                      maxCount={10}
                      onRemove={(file) => {
                        setPdfUploads((prevUploads) =>
                          prevUploads.filter(
                            (upload) => upload.file.name !== file.name
                          )
                        );
                      }}
                      beforeUpload={(file, fileList) => {
                        if (fileList.length > 10) {
                          ToastDispatch({
                            isOpen: true,
                            type: "Error",
                            message: "You can only upload up to 10 PDF files!",
                          });
                          return Upload.LIST_IGNORE;
                        }
                        const isLt10MB = file.size / 1024 / 1024 < 50;
                        if (!isPdfFile(file)) {
                          message.error("You can only upload PDF files!");
                          return Upload.LIST_IGNORE; // Prevent the file from being added to the list
                        }
                        if (!isLt10MB) {
                          message.error("PDF must be smaller than 50MB!");
                          return Upload.LIST_IGNORE;
                        }

                        // Trigger the uploadFile function directly
                        const cancelToken = axios.CancelToken.source();
                        setPdfUploads((prevUploads) => [
                          ...prevUploads,
                          {
                            file,
                            progress: 0,
                            cancelToken,
                            remainingTime: "Calculating...",
                            isUploading: false,
                          },
                        ]);

                        return false; // Prevent auto-upload by Upload component
                      }}
                    >
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">Select PDFs to upload</p>
                      <p className="ant-upload-hint">
                        Maximum file size 50MB, format .pdf (Max 10 files).
                      </p>
                    </Upload.Dragger>
                  )}
                </Form.Item>
              )}
            </>
          )}
          {pdfUploads.length > 0 &&
            pdfUploads.some((upload) => upload.isUploading) && (
              <Card>
                {pdfUploads.map(
                  (upload: any, index: number) =>
                    upload.isUploading && (
                      <React.Fragment key={upload.file.name}>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Spin
                            size="large"
                            indicator={<LoadingOutlined spin />}
                          />
                          <div style={{ width: "100%", marginLeft: "10px" }}>
                            <Text>{upload.file.name}</Text>
                            {upload.progress > 0 && (
                              <div
                                style={{
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                }}
                              >
                                <Progress
                                  percent={upload.progress}
                                  status={
                                    upload.isUploading ? "active" : "success"
                                  }
                                  style={{ width: "100%" }}
                                />
                                <Button
                                  type="text"
                                  icon={<CloseOutlined />}
                                  onClick={() =>
                                    cancelUpload("pdf", upload.file.name)
                                  }
                                  style={{
                                    marginLeft: "10px",
                                  }}
                                ></Button>
                              </div>
                            )}
                          </div>
                        </div>
                        {index < pdfUploads.length - 1 && <Divider />}
                      </React.Fragment>
                    )
                )}
              </Card>
            )}
        </Form>
      </Drawer>

      {/* AssetViewerModal for viewing videos and PDFs */}
      <AssetViewerModal
        isOpen={isViewerModalOpen}
        onClose={closeViewerModal}
        contentType={viewerContentType}
        title={viewerTitle}
        assetId={lectureId}
        pdfPassword={pdfPassword}
      />
    </>
  );
};

export default UploadAssetDrawer;
